import { COLOR } from 'app-v2/design-tokens'
import { Icon } from 'app-v2/shared/features/icon'
import { tokens } from 'app-v2/shared/features/icon/utils/tokens'
import { Close } from 'app/assets/icons'
import { AlertModal } from 'app/common/modals/alert'
import { OpeningStatus } from 'app/common/restaurant'
import { H2, Text } from 'app/common/typography'
import { UnderlinedText } from 'app/common/underlined-text'
import {
    CancelOrderModal,
    OrderPreparation,
    OrderStatus
} from 'app/ecommerce/common'
import { ReuseOrder } from 'app/ecommerce/common/components/reuse-order'
import React, { Fragment, useEffect, useState } from 'react'
import type { IntlShape } from 'react-intl'
import { FormattedMessage, injectIntl } from 'react-intl'
import { useSelector } from 'react-redux'
import { colors as oldColors } from 'shared/assets/styles'
import type { EventMetadata } from 'shared/store/metadata'
import { eventsDefinitions } from 'shared/store/metadata'
import type { RootState } from 'shared/store/reducers'
import type { CacOrder, DeviceCheck } from 'shared/types'
import {
    errors,
    formatDateToDayMonthYear,
    formatDateToHoursAndMinutes,
    formatPrice,
    isDefined,
    orderPickUpType
} from 'shared/utils'
import {
    formatTableToDisplay,
    getTableOrderValuesFromPickUpInfo
} from 'shared/utils-v2'
import {
    isOrderCancelled,
    isOrderCancelledByUser,
    isOrderDelivered,
    isOrderLaunched
} from 'shared/utils/ecommerce'
import styled from 'styled-components'

const { grey30, shadow } = oldColors
const {
    DRIVE,
    TABLESERVICE,
    ONSITE,
    TAKEAWAY,
    PARKING,
    TABLE_ORDER
} = orderPickUpType

type Props = {
    order: CacOrder,
    sendMeta: EventMetadata => void,
    onClick?: (order: CacOrder) => void,
    onClickDetails?: () => void,
    discard: (order: CacOrder) => void,
    cancelOrder: (
        id: string,
        token: string,
        deviceCheckPayload: DeviceCheck
    ) => void,
    intl: IntlShape
}

type State = {
    error?: string,
    width: number
}

const _CCOrderStatusCard = ({
    order,
    sendMeta,
    onClick,
    onClickDetails,
    discard,
    cancelOrder,
    intl
}: Props) => {
    const {
        id,
        token,
        orbStatus,
        orderStatus,
        discarded,
        pickUpType,
        deliveredAt,
        restaurant,
        finalPrice,
        creationDate,
        orderNumber
    } = order
    const { secondsBeforeOrderDelivered } = useSelector(
        (state: RootState) => state.applicationState
    )
    const [state: State, setState] = useState({
        error: undefined,
        width: window.innerWidth
    })
    const [cancelOrderModal, setCancelOrderModal] = useState(false)
    const { error } = state

    useEffect(() => {
        const updateWindowDimensions = () => {
            setState(state => ({ ...state, width: window.innerWidth }))
        }
        window.addEventListener('resize', updateWindowDimensions)
        return () => {
            window.removeEventListener('resize', updateWindowDimensions)
        }
    }, [])

    const renderOrderNumber = () => {
        const title =
            order.pickUpType === TABLE_ORDER
                ? 'clickandcollect.delivery.choice.tableorder'
                : 'orb.card.order.light'
        return (
            <div>
                <FormattedMessage id={title}>
                    {(text: string) => <Text>{text}</Text>}
                </FormattedMessage>
                {isDefined(orderNumber) && <StyledH2>{orderNumber}</StyledH2>}
            </div>
        )
    }

    const renderPickUp = () => {
        return (
            <PickUpTypeContainer>
                {!!pickUpType && isDefined(finalPrice) && (
                    <Text>
                        <FormattedMessage
                            id="orb.pick.up.price"
                            values={{
                                pickup: intl.formatMessage(
                                    {
                                        id: renderPickUpType()
                                    },
                                    {
                                        pickUpInfo: !!order.pickUpInfo
                                            ? formatTableToDisplay(
                                                  getTableOrderValuesFromPickUpInfo(
                                                      order.pickUpInfo
                                                  ).table
                                              )
                                            : ''
                                    }
                                ),
                                //$FlowFixMe
                                price: formatPrice(finalPrice)
                            }}
                        />
                    </Text>
                )}
            </PickUpTypeContainer>
        )
    }

    const renderInfo = () => {
        return (
            <FormattedMessage
                id={
                    pickUpType === TABLESERVICE
                        ? 'clickandcollect.orderstatus.onsite'
                        : pickUpType === DRIVE
                        ? 'clickandcollect.orderstatus.drive'
                        : 'clickandcollect.orderstatus.takeaway'
                }>
                {(text: string) => (
                    <StyledText isDrive={pickUpType === DRIVE}>
                        {text}
                    </StyledText>
                )}
            </FormattedMessage>
        )
    }

    const renderOrderStatus = () => (
        <OrderStatus
            isDelivered={isOrderDelivered(
                order.orbStatus,
                order.deliveredAt,
                secondsBeforeOrderDelivered
            )}
            isCancelled={isOrderCancelled(orderStatus, orbStatus)}
            marginTop={!discarded}
        />
    )

    const renderRestaurant = () => {
        const { id, name, cacStatus, openingCacHour, closingCacHour } =
            restaurant || {}
        return (
            <Fragment>
                <HorizontalSeparator />
                <Left>
                    <Icon
                        code={tokens.marker}
                        marginLeft={0}
                        marginRight={0}
                        width={33}
                        height={40}
                    />
                    <Infos>
                        <SubText>{name}</SubText>
                        {cacStatus && (
                            <Status>
                                <OpeningStatusContainer>
                                    <OpeningStatus
                                        id={id}
                                        status={cacStatus}
                                        openingHour={openingCacHour}
                                        closingHour={closingCacHour}
                                    />
                                </OpeningStatusContainer>
                            </Status>
                        )}
                    </Infos>
                </Left>
            </Fragment>
        )
    }

    const renderCloseButton = () => {
        return (
            <CloseContainer onClick={() => discardOrder()}>
                <Close size={20} color={COLOR.PRIMARY} />
            </CloseContainer>
        )
    }

    const renderCreationDateTime = () => {
        if (!creationDate) {
            return null
        }
        return (
            <Text>
                {formatDateToDayMonthYear(creationDate)},
                {formatDateToHoursAndMinutes(creationDate)}
            </Text>
        )
    }

    const discardOrder = async () => {
        let errorMessage = undefined
        try {
            await discard(order)
        } catch (error) {
            switch (error) {
                case errors.NETWORK:
                    errorMessage = 'error.network.alert.message'
                    break
                default:
                    errorMessage = 'form.error.default'
                    break
            }
        } finally {
            setState({ ...state, error: errorMessage })
        }
    }

    const renderPickUpType = () => {
        switch (pickUpType) {
            case TABLESERVICE:
            case ONSITE:
                return 'clickandcollect.delivery.choice.onsite'
            case TAKEAWAY:
                return 'clickandcollect.delivery.choice.takeaway'
            case DRIVE:
                return 'clickandcollect.delivery.choice.drive'
            case PARKING:
                return 'clickandcollect.delivery.choice.parking'
            case TABLE_ORDER:
                return 'clickandcollect.pickupinfo.tableorder'
            default:
                return ''
        }
    }

    const isTableOrder = pickUpType === TABLE_ORDER

    const isLaunched = isOrderLaunched(orderStatus, orbStatus)
    const isCancelled = isOrderCancelled(orderStatus, orbStatus)
    const isCancelledByUser = isOrderCancelledByUser(orderStatus)
    const isDelivered = isOrderDelivered(
        orbStatus,
        deliveredAt,
        secondsBeforeOrderDelivered
    )
    const waitingToPrepare = !isLaunched && !isCancelled

    return (
        <div>
            <MainContainer
                disabled={!onClick}
                onClick={() => {
                    onClick && onClick(order)
                }}>
                <TopContainer>
                    <LeftContainer>
                        {renderOrderNumber()}
                        {renderPickUp()}
                    </LeftContainer>
                    <RightContainer>
                        {(isCancelled || isDelivered) &&
                            !discarded &&
                            renderCloseButton()}
                        {discarded && renderCreationDateTime()}
                        <UnderlinedText
                            textKey="clickandcollect.listorders.detail"
                            onClick={onClickDetails}
                        />
                    </RightContainer>
                </TopContainer>
                {waitingToPrepare && renderInfo()}
                {discarded ? (
                    <PassedStatusContainer>
                        {renderOrderStatus()}
                        {!isTableOrder && <ReuseOrder order={order} mobile />}
                    </PassedStatusContainer>
                ) : isCancelledByUser ? (
                    renderOrderStatus()
                ) : (
                    pickUpType !== DRIVE && (
                        <>
                            <OrderPreparation
                                order={order}
                                onClick={() => {
                                    sendMeta({
                                        ...eventsDefinitions.GA4_COMMAND_CANCELLATION,
                                        page_template: 'Kingdom'
                                    })
                                    setCancelOrderModal(true)
                                }}
                                cancelOrder={cancelOrder}
                                fromList
                            />
                            <CancelOrderModal
                                id={id}
                                token={token}
                                cancelOrder={cancelOrder}
                                cancelOrderModal={cancelOrderModal}
                                setCancelOrderModal={setCancelOrderModal}
                                setError={error => {
                                    setState(state => ({ ...state, error }))
                                }}
                            />
                        </>
                    )
                )}
                {renderRestaurant()}
            </MainContainer>
            {!!error && (
                <AlertModal
                    alertTitleKey="error"
                    alertContentKey={error}
                    isOpen={!!error}
                    handleClose={() => setState({ ...state, error: undefined })}
                />
            )}
        </div>
    )
}

const MainContainer = styled.div`
    border-radius: 10px;
    justify-content: space-between;
    flex: 1;
    display: flex;
    flex-direction: column;
    padding: 20px;
    padding-bottom: 0;
    box-shadow: 0 0 15px ${shadow};
    margin-bottom: 30px;
`

const LeftContainer = styled.div`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
`

const RightContainer = styled.div`
    display: flex;
    flex-direction: column;
    align-items: flex-end;
`

const TopContainer = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    margin: 0 5px 10px;
`

const PassedStatusContainer = styled.div`
    display: block;
    width: 100%;
    margin: auto;
    max-width: 400px;
`

const PickUpTypeContainer = styled.div`
    display: flex;
    flex-direction: row;
`

const HorizontalSeparator = styled.div`
    width: 95%;
    height: 1px;
    background: ${grey30};
    margin: 0 10px;
`

const CloseContainer = styled.div`
    padding: 5px 5px 0;
`

const SubText = styled(Text)`
    text-transform: uppercase;
    font-weight: bold;
    margin-right: 5px;
`

const Left = styled.div`
    display: flex;
    flex-direction: row;
    align-items: flex-start;
    justify-content: flex-start;
    margin: 20px 10px;
`

const Infos = styled.div`
    margin-left: 10px;
    margin-top: -5px;
`

const Status = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: space-between;
`

const OpeningStatusContainer = styled.div`
    margin-right: 10px;
`

const StyledText = styled(Text)`
    ${props => props.isDrive && `margin-bottom: 10px;`};
    font-weight: bold;
`

const StyledH2 = styled(H2)`
    text-transform: uppercase;
`

export const CCOrderStatusCard = injectIntl(_CCOrderStatusCard)
